import React, { useMemo } from "react";
import { DefaultButton, Dropdown, Panel, PanelType, PrimaryButton, Stack, Text } from "@fluentui/react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextField from "../../../Forms/TextField";
import TextCKEditor from "../../../Forms/TextCKEditor";

const CKEDITOR_FLOATING_UI_SELECTOR = ".cke_dialog, .cke_dialog_background_cover, .cke_panel";

const validationSchema = Yup.object().shape({
  benefitCategoryId: Yup.number().required("Benefit category is required."),
  benefitTypeId: Yup.number().required("Benefit type is required."),
  providerName: Yup.string().trim().required("Provider name is required."),
  benefitDetail: Yup.string().required("Benefit detail is required."),
  featuredOrder: Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null) {
        return null;
      }
      return Number.isNaN(value) ? null : value;
    })
    .min(0, "Featured order must be 0 or greater."),
});

const BenefitFormModal = ({
  isOpen,
  onDismiss,
  onSave,
  saving,
  benefit,
  categories,
  types,
  callToActions,
}) => {
  const categoryOptions = useMemo(
    () =>
      (categories || []).map((category) => ({
        key: category.benefitCategoryId,
        text: category.benefitCategory,
      })),
    [categories]
  );

  const typeOptions = useMemo(
    () =>
      (types || []).map((type) => ({
        key: type.benefitTypeId,
        text: type.benefitType,
        data: type,
      })),
    [types]
  );

  const callToActionOptions = useMemo(
    () => [
      { key: 0, text: "None" },
      ...(callToActions || []).map((cta) => ({
        key: cta.callToActionId,
        text: cta.title,
      })),
    ],
    [callToActions]
  );

  if (!isOpen || !benefit) {
    return null;
  }

  const initialValues = {
    benefitId: benefit.benefitId,
    cobrandId: benefit.cobrandId,
    benefitCategoryId: benefit.benefitCategoryId,
    benefitTypeId: benefit.benefitTypeId,
    providerName: benefit.providerName || "",
    thumbnailUrl: benefit.thumbnailUrl || "",
    benefitDetail: benefit.benefitDetail || "",
    callToActionId: benefit.callToActionId ?? 0,
    featuredOrder: benefit.featuredOrder != null ? benefit.featuredOrder : "",
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={async (values, actions) => {
        await onSave(values);
        actions.setSubmitting(false);
      }}
    >
      {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting, isValid }) => {
        const filteredTypeOptions = typeOptions.filter(
          (option) => option.data.benefitCategoryId === values.benefitCategoryId
        );

        return (
          <Panel
            isOpen={isOpen}
            onDismiss={onDismiss}
            type={PanelType.medium}
            headerText={values.benefitId ? "Edit Benefit" : "Add Benefit"}
            isFooterAtBottom
            closeButtonAriaLabel="Close"
            // CKEditor dialogs (e.g. Link) render on document.body outside the Panel, so the
            // Panel must neither steal their focus nor treat clicks in them as outer clicks.
            focusTrapZoneProps={{ forceFocusInsideTrap: false }}
            onOuterClick={(ev) => {
              if (ev.target instanceof Element && ev.target.closest(CKEDITOR_FLOATING_UI_SELECTOR)) {
                return;
              }
              onDismiss();
            }}
            onRenderFooterContent={() => (
              <Stack horizontal tokens={{ childrenGap: 8 }} horizontalAlign="end">
                <PrimaryButton
                  text={saving || isSubmitting ? "Saving..." : "Save"}
                  onClick={handleSubmit}
                  disabled={!isValid || saving || isSubmitting}
                />
                <DefaultButton text="Cancel" onClick={onDismiss} disabled={saving || isSubmitting} />
              </Stack>
            )}
          >
            <Form>
              <Stack tokens={{ childrenGap: 12 }}>
                <Dropdown
                  label="Benefit Category"
                  placeholder="Select a category"
                  options={categoryOptions}
                  selectedKey={values.benefitCategoryId}
                  onChange={(_, option) => {
                    const newValue = option ? option.key : null;
                    setFieldValue("benefitCategoryId", newValue);
                    setFieldValue("benefitTypeId", null);
                  }}
                  required
                  errorMessage={touched.benefitCategoryId && errors.benefitCategoryId}
                />

                <Dropdown
                  label="Benefit Type"
                  placeholder="Select a type"
                  disabled={!values.benefitCategoryId}
                  options={filteredTypeOptions}
                  selectedKey={values.benefitTypeId}
                  onChange={(_, option) => setFieldValue("benefitTypeId", option ? option.key : null)}
                  required
                  errorMessage={touched.benefitTypeId && errors.benefitTypeId}
                />

                <TextField label="Provider Name" name="providerName" required />

                <TextField label="Thumbnail URL" name="thumbnailUrl" />

                <Dropdown
                  label="Call To Action"
                  options={callToActionOptions}
                  selectedKey={values.callToActionId ?? 0}
                  onChange={(_, option) => setFieldValue("callToActionId", option ? option.key : 0)}
                />

                <TextField
                  label="Featured Order"
                  name="featuredOrder"
                  type="number"
                  min={0}
                  step={1}
                />

                <div>
                  <Text variant="mediumPlus" styles={{ root: { marginBottom: 4 } }}>
                    Benefit Detail <Text variant="small" styles={{ root: { color: "#605e5c", fontWeight: 400, display: "inline" } }}>(supports HTML formatting)</Text>
                  </Text>
                  <TextCKEditor
                    value={values.benefitDetail ? values.benefitDetail : ""}
                    onChange={(val) => setFieldValue('benefitDetail', val)}
                    className="mt10"
                  />
                  {touched.benefitDetail && errors.benefitDetail && (
                    <Text variant="small" styles={{ root: { color: "#a4262c" } }}>
                      {errors.benefitDetail}
                    </Text>
                  )}
                </div>
              </Stack>
            </Form>
          </Panel>
        );
      }}
    </Formik>
  );
};

export default BenefitFormModal;

